import React, { useState, useEffect } from 'react';
import { saleService } from '../api/saleService';
import './Sales.css';

function Sales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    source: '',
    date_from: '',
    date_to: '',
    search: '',
    per_page: 15,
  });

  useEffect(() => {
    fetchSales();
  }, [filters]);

  const fetchSales = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page,
      };
      const response = await saleService.getSales(params);
      setSales(response.data || []);
      setPagination({
        current_page: response.current_page,
        last_page: response.last_page,
        total: response.total,
      });
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDownloadInvoice = async (saleId) => {
    try {
      const blob = await saleService.downloadInvoice(saleId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_vente_${String(saleId).padStart(6, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Échec du téléchargement de la facture');
    }
  };

  const clearFilters = () => {
    setFilters({
      source: '',
      date_from: '',
      date_to: '',
      search: '',
      per_page: 15,
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      accepted: 'status-accepted',
      shipped: 'status-shipped',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return statusClasses[status] || 'status-default';
  };

  const getPaymentStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'payment-pending',
      accepted: 'payment-accepted',
      failed: 'payment-failed',
      refunded: 'payment-refunded',
    };
    return statusClasses[status] || 'payment-default';
  };

  return (
    <div className="sales-container">
      <div className="sales-header">
        <h1>Ventes</h1>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">Total des ventes:</span>
            <span className="stat-value">{pagination.total || 0}</span>
          </div>
        </div>
      </div>

      <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
        Toutes les ventes effectuées depuis le site web et les transactions du point de vente.
      </p>

      {/* Filters */}
      <div className="sales-filters">
        <div className="filter-row">
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Rechercher par client, email ou facture..."
            className="filter-input search-input"
          />

          <select
            name="source"
            value={filters.source}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Toutes les sources</option>
            <option value="store">Magasin (POS)</option>
            <option value="website">Site web</option>
          </select>

          <input
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilterChange}
            className="filter-input"
          />

          <input
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilterChange}
            className="filter-input"
          />

          <button onClick={clearFilters} className="btn-clear-filters">
            Effacer les filtres
          </button>
        </div>
      </div>

      {/* Sales Table */}
      {loading ? (
        <div className="loading-message">Chargement des ventes...</div>
      ) : sales.length === 0 ? (
        <div className="empty-message">Aucune vente trouvée</div>
      ) : (
        <div className="sales-table-container">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Vente #</th>
                <th>Facture</th>
                <th>Date</th>
                <th>Client</th>
                <th>Source</th>
                <th>Articles</th>
                <th>Total</th>
                <th>Paiement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => (
                <tr key={sale.id}>
                  <td className="order-number">
                    #{String(sale.id).padStart(6, '0')}
                  </td>
                  <td className="order-number">
                    {sale.invoice_uuid ? sale.invoice_uuid.substring(0, 8) + '...' : 'N/A'}
                  </td>
                  <td className="order-date">
                    <div>{new Date(sale.created_at).toLocaleDateString()}</div>
                    <div className="order-time">
                      {new Date(sale.created_at).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="client-info">
                    {sale.client ? (
                      <div>
                        <div className="client-name">
                          {sale.client.first_name} {sale.client.last_name}
                        </div>
                        <div className="client-contact">{sale.client.email}</div>
                      </div>
                    ) : (
                      <div>
                        <div className="client-name">
                          {sale.guest_name || 'Client de passage'}
                        </div>
                        {sale.guest_email && (
                          <div className="client-contact">{sale.guest_email}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="source-badge">
                    <span className={`badge badge-${sale.source}`}>
                      {sale.source === 'store' ? '🏪 Magasin' : '🌐 Site web'}
                    </span>
                  </td>
                  <td className="items-count">{sale.sale_items_count || sale.saleItems?.length || 0}</td>
                  <td className="order-total">{parseFloat(sale.total).toFixed(2)} DH</td>
                  <td>
                    {sale.payment_method && (
                      <div className="payment-method">
                        {sale.payment_method.charAt(0).toUpperCase() +
                          sale.payment_method.slice(1)}
                      </div>
                    )}
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handleDownloadInvoice(sale.id)}
                      className="btn-download-invoice"
                      title="Télécharger la facture"
                    >
                      📄 Facture
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="pagination">
          <button
            onClick={() => fetchSales(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
            className="pagination-btn"
          >
            Précédent
          </button>
          <span className="pagination-info">
            Page {pagination.current_page} sur {pagination.last_page}
          </span>
          <button
            onClick={() => fetchSales(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
            className="pagination-btn"
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
}

export default Sales;
